import { Clock, MathUtils, Vector3 } from "three";
import { DateTime } from "luxon";

import "./style.css";

import { createScene } from "./scene";
import { createEarth } from "./earth";
import { createAtmosphere } from "./atmosphere";
import { createStars } from "./stars";
import { createMilkyWay } from "./milkyway";
import { computeSolarState } from "./sun";
import { CityLayer, type ZoneCityDef } from "./cities";
import { loadCoastlines, loadCountryBorders } from "./countries";
import { LazyLineLayer } from "./lines";
import { createTerminatorLine } from "./terminator";
import { loadZoneFills } from "./zonefills";
import { createLandMesh, type LandMesh } from "./landmesh";
import { loadZoneLines, type ZoneLines } from "./zonelines";
import { ClockOverlay } from "./clock";
import { CAMERA_DISTANCE, LAYERS, type LayerId } from "./constants";
import { latLngToVec3 } from "./geo";
import { resolveObserverLocation } from "./observer";
import { loadTzRules } from "./tzrules";

async function boot() {
  const canvas = document.getElementById("globe-canvas") as HTMLCanvasElement;
  if (!canvas) throw new Error("#globe-canvas not found");

  const ctx = createScene(canvas);
  // The app's own time-zone rules, before any clock is drawn.
  const tzRules = loadTzRules();

  // Build scene graph
  const [earth] = await Promise.all([createEarth(), tzRules]);
  const atmosphere = createAtmosphere();
  const stars = createStars();
  const milkyWay = createMilkyWay();

  ctx.scene.add(milkyWay);
  ctx.scene.add(earth.mesh);
  ctx.scene.add(atmosphere.mesh);
  for (const s of stars) ctx.scene.add(s);

  // Time zones layer overlays. Data loads on first show.
  const terminator = createTerminatorLine();
  // Zone fills and live boundary lines, loaded on first use.
  let zoneLines: ZoneLines | null = null;
  let landMesh: LandMesh | null = null;
  ctx.scene.add(terminator.mesh);
  const countryBorders = new LazyLineLayer(
    loadCountryBorders,
    (m) => ctx.scene.add(m),
    { colors: [0xe8e4d8, 0x8a8f99], opacity: [0.35, 0.6] },
  );
  const coastlines = new LazyLineLayer(
    loadCoastlines,
    (m) => ctx.scene.add(m),
    { colors: [0x8fb3cc, 0x7f9cb3], opacity: [0.45, 0.7] },
  );

  // Debug: expose refs on window for quick inspection in devtools
  (window as unknown as { __atlas: unknown }).__atlas = {
    camera: ctx.camera,
    controls: ctx.controls,
  };

  // Overlay + HUD
  const clockOverlay = new ClockOverlay();
  const cityLayer = new CityLayer(
    document.getElementById("city-layer")!,
    ctx.camera,
    ctx.renderer,
  );

  // ---- Initial camera framing: face the user's city, not just their
  //      meridian. IANA-zone lookup resolves Europe/Vilnius → Vilnius
  //      coords, etc. Falls back to a meridian estimate on cache miss. ----
  const observer = resolveObserverLocation();
  const initialLat = observer.lat;
  const initialLng = observer.lng;
  frameCamera(initialLat, initialLng);
  clockOverlay.setObserverCoord(initialLat, initialLng);

  function frameCamera(lat: number, lng: number): void {
    const dir = latLngToVec3(lat, lng, 1, new Vector3());
    ctx.camera.position.copy(dir).multiplyScalar(CAMERA_DISTANCE);
    ctx.controls.target.set(0, 0, 0);
    ctx.controls.update();
  }

  // ---- Theme (light by default, dark observatory on request) ----
  type Theme = "light" | "dark";
  let layersReady = false; // the layer controls below are wired
  const themeBtn = document.getElementById("toggle-theme") as HTMLButtonElement;
  const themeLabel = document.getElementById("theme-label") as HTMLSpanElement;
  const storedTheme = localStorage.getItem("ta:theme2") as Theme | null;
  let theme: Theme = storedTheme === "dark" ? "dark" : "light";
  // Shader and overlay colour sets: 1 = light, 0 = dark.
  const themeIndex = () => (theme === "light" ? 1 : 0);

  function applyTheme(t: Theme): void {
    theme = t;
    localStorage.setItem("ta:theme2", t);
    const dark = t === "dark";
    const k = themeIndex();
    document.body.classList.toggle("theme-dark", dark);
    themeBtn.setAttribute("aria-pressed", dark ? "true" : "false");
    // The button offers the other theme.
    themeLabel.textContent = dark ? "Light mode" : "Dark mode";
    earth.setTheme(k);
    atmosphere.setTheme(k);
    terminator.setTheme(k);
    countryBorders.setTheme(k);
    coastlines.setTheme(k);
    landMesh?.setTheme(k);
    if (layersReady) updateZoneLines();
    // Stars and the Milky Way belong to the dark sky only.
    milkyWay.visible = dark;
    for (const s of stars) s.visible = dark;
  }
  themeBtn.addEventListener("click", () =>
    applyTheme(theme === "light" ? "dark" : "light"),
  );
  applyTheme(theme);

  // ---- Timezone borders: the T key / button, on any layer. A line
  //      shows wherever two zones on land keep a different time right
  //      now. ----
  const tzBtn = document.getElementById("toggle-tz") as HTMLButtonElement;
  let tzChoice = false; // what T last set
  let zonesLayer = false;
  let tzLines = false;

  function updateZoneLines(): void {
    const light = theme === "light";
    // On the pastel map a soft slate line between fills; over the
    // photographic layers the cyan / indigo that stands apart from the
    // terminator.
    const [opacity, rgb]: [number, [number, number, number]] = zonesLayer
      ? light ? [0.55, [0.2, 0.23, 0.29]] : [0.75, [0.95, 0.97, 1.0]]
      : light ? [0.9, [0.36, 0.44, 0.84]] : [0.9, [0.35, 0.78, 0.83]];
    if (!zoneLines) return;
    zoneLines.setStyle(rgb, opacity);
    zoneLines.mesh.visible = tzLines;
  }

  let zoneLinesLoading: Promise<void> | null = null;
  function loadLines(): Promise<void> {
    if (!zoneLinesLoading) tzBtn.setAttribute("aria-busy", "true");
    zoneLinesLoading ??= loadZoneLines(earth.waterTexture)
      .then((l) => {
        zoneLines = l;
        ctx.scene.add(l.mesh);
        updateZoneLines();
      })
      .catch((err) => {
        zoneLinesLoading = null;
        console.error("zone lines load failed", err);
      })
      .finally(() => tzBtn.removeAttribute("aria-busy"));
    return zoneLinesLoading;
  }

  function showTimezones(v: boolean): void {
    tzLines = v;
    tzBtn.setAttribute("aria-pressed", v ? "true" : "false");
    if (v) void loadLines();
    updateZoneLines();
  }
  function toggleTimezones() {
    const v = !tzLines;
    tzChoice = v;
    showTimezones(v);
  }
  tzBtn.addEventListener("click", toggleTimezones);

  // Zone cities load once, the first time the Time zones layer opens.
  let zoneCities: Promise<void> | null = null;
  function loadZoneCities(): Promise<void> {
    zoneCities ??= fetch("/data/zone-cities.json")
      .then((r) => {
        if (!r.ok) throw new Error(`zone-cities: ${r.status}`);
        return r.json() as Promise<{ cities: ZoneCityDef[] }>;
      })
      .then((d) => {
        cityLayer.addZoneCities(d.cities);
        updateHud(DateTime.utc());
      })
      .catch((err) => {
        zoneCities = null;
        console.error(err);
      });
    return zoneCities;
  }

  // Zone fills and the land mesh coloured from them load once, the
  // first time the Time zones layer opens.
  let zoneFillsLoading: Promise<void> | null = null;
  function loadFills(): Promise<void> {
    zoneFillsLoading ??= loadZoneFills()
      .then((zones) => {
        const land = createLandMesh(zones, earth.nightTexture, themeIndex());
        land.mesh.visible = zonesLayer;
        ctx.scene.add(land.mesh);
        landMesh = land;
        earth.setLandMesh(true);
      })
      .catch((err) => {
        zoneFillsLoading = null;
        console.error("zone fills load failed", err);
      });
    return zoneFillsLoading;
  }

  function applyLayer(id: LayerId): void {
    void earth.setLayer(id);
    zonesLayer = LAYERS[id].zones === 1;
    if (zonesLayer) void loadFills();
    terminator.mesh.visible = zonesLayer;
    if (landMesh) landMesh.mesh.visible = zonesLayer;
    cityLayer.setZonesMode(zonesLayer);
    // The Time zones map shows its boundaries as edges between colours;
    // the separate lines stay the T key's choice.
    showTimezones(tzChoice);
    countryBorders
      .setVisible(zonesLayer)
      .catch((err) => console.error("country borders load failed", err));
    coastlines
      .setVisible(zonesLayer)
      .catch((err) => console.error("coastlines load failed", err));
    if (zonesLayer) void loadZoneCities();
  }

  // ---- Layer picker (Satellite / Time zones / Physical / Night) ----
  const layerInputs = Array.from(
    document.querySelectorAll<HTMLInputElement>('input[name="layer"]'),
  );
  const storedLayer = localStorage.getItem("ta:layer") as LayerId | null;
  const initialLayer: LayerId =
    storedLayer && storedLayer in LAYERS ? storedLayer : "timezones";
  for (const input of layerInputs) {
    input.checked = input.value === initialLayer;
    input.addEventListener("change", () => {
      if (!input.checked) return;
      const id = input.value as LayerId;
      localStorage.setItem("ta:layer", id);
      applyLayer(id);
    });
  }
  applyLayer(initialLayer);
  layersReady = true;

  // ---- Controls / keyboard ----
  const resetBtn = document.getElementById("reset-view") as HTMLButtonElement;
  resetBtn.addEventListener("click", () => frameCamera(initialLat, initialLng));

  window.addEventListener("keydown", (e) => {
    if (e.key === "r" || e.key === "R") frameCamera(initialLat, initialLng);
    if (e.key === "t" || e.key === "T") toggleTimezones();
    if (e.key === "d" || e.key === "D") {
      applyTheme(theme === "light" ? "dark" : "light");
    }
  });

  // ---- Animation loop ----
  const clock = new Clock();
  const sunDir = new Vector3();
  let lastHudUpdate = 0;

  function updateSolar(now: Date): Vector3 {
    const solar = computeSolarState(now, sunDir);
    earth.setSunDirection(solar.direction);
    landMesh?.setSunDirection(solar.direction);
    terminator.setSunDirection(solar.direction);
    atmosphere.setSunDirection(solar.direction);
    earth.material.uniforms.uTime.value = clock.getElapsedTime();
    return solar.direction;
  }

  function updateHud(nowDt: DateTime): void {
    const solar = computeSolarState(nowDt.toJSDate());
    clockOverlay.update(nowDt, solar);
    cityLayer.updateClocks(nowDt);
    landMesh?.update(nowDt);
    zoneLines?.update(nowDt);
  }

  // Track the current observer coord based on where the camera looks.
  const camDir = new Vector3();
  function updateObserverCoord(): void {
    ctx.camera.getWorldDirection(camDir).negate(); // points from earth → camera
    // Convert unit vector back to lat/lng
    const lat = MathUtils.radToDeg(Math.asin(camDir.y));
    const lng = wrap180(
      MathUtils.radToDeg(Math.atan2(camDir.z, -camDir.x)) - 180,
    );
    clockOverlay.setObserverCoord(lat, lng);
  }

  function wrap180(lng: number): number {
    return ((((lng + 180) % 360) + 360) % 360) - 180;
  }

  // Prime HUD immediately to avoid a flash of placeholder text.
  updateHud(DateTime.utc());

  function tick() {
    const now = new Date();
    const nowDt = DateTime.fromJSDate(now);

    const dir = updateSolar(now);
    ctx.controls.update();

    // Lock the starfield + Milky Way to the camera so they feel like an
    // infinitely-distant backdrop rather than a co-rotating sphere.
    milkyWay.position.copy(ctx.camera.position);
    for (const s of stars) s.position.copy(ctx.camera.position);

    cityLayer.updatePositions(dir);

    // HUD updates at 4 Hz — the second hand moves smoothly without
    // wasting DOM writes every frame.
    if (now.getTime() - lastHudUpdate > 250) {
      updateHud(nowDt);
      updateObserverCoord();
      lastHudUpdate = now.getTime();
    }

    ctx.renderer.render(ctx.scene, ctx.camera);
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

boot().catch((err) => {
  console.error(err);
  document.body.innerHTML = `
    <div style="position:fixed;inset:0;display:grid;place-items:center;
                font-family:'IBM Plex Mono',monospace;color:#e8e4d8;
                background:#050608;padding:40px;text-align:center;">
      <div>
        <div style="font-family:Fraunces,serif;font-size:28px;margin-bottom:12px;">
          Instrument offline
        </div>
        <code style="color:#ff9d4a;">${(err as Error).message}</code>
      </div>
    </div>
  `;
});
